import { GenericAbortSignal } from 'axios';

import { HighCostMedicationTier } from 'models/HighCostMedicationList';
import http from 'services/http';

export const highCostMedicationTierQueryKeys = {
  base: ['highCostMedicationTiers'],
  index: () => ['highCostMedicationTiers', 'index'],
};

// Unpaginated on purpose — a cost tier key is one contract's tier vocabulary, not a table. Returns
// unarchived tiers in position order, or an empty array when the client has no list.
export async function indexHighCostMedicationTiers(signal?: GenericAbortSignal) {
  return http
    .get<{ data: HighCostMedicationTier[] }>('/high_cost_medication_tiers', { signal })
    .then((response) => response.data);
}

// `description` and `position` are the writable parts of a tier — the two things about it that are
// authored rather than derived from the medications carrying it. Its label and whether it exists at
// all are consequences of those medications, so this payload cannot add, rename or retire one: the
// API permits `id`, `description` and `position` and drops anything else. Declared here rather than
// imported from the form so the service layer does not depend on a component; the shared form's
// CostTierUpdate is structurally identical, so a caller passes one straight through.
export type HighCostMedicationTierUpdate = {
  id: string;
  description: string | null;
  position: number;
};

// Descriptions and order only. No list id in the path and none in the body: the API resolves the
// acting client's list through its policy scope, and a tier id outside it 404s. The whole key is
// sent on every save — one drag rewrites every position — and the response is the refreshed key in
// its new order, the same shape the index returns. See design 003, "Customer API".
export async function updateHighCostMedicationTiers({ tiers }: { tiers: HighCostMedicationTierUpdate[] }) {
  return http
    .patch<{ data: HighCostMedicationTier[] }>('/high_cost_medication_tiers', { tiers })
    .then((response) => response.data);
}
